"use client";

import { Fragment, type CSSProperties, type ReactNode } from "react";
import type { MarkdownBlockNode, MarkdownInlineNode, MarkdownTableAlign } from "@/lib/types";
import { useI18n } from "./I18nProvider";
import { MarkdownCodeBlock } from "./MarkdownCodeBlock";
import { MarkdownInlineCode } from "./MarkdownInlineCode";

export function MarkdownBody({ nodes, className }: { nodes: MarkdownBlockNode[]; className?: string }) {
  const { t } = useI18n();
  return (
    <div className={className ? `markdown-body ${className}` : "markdown-body"}>
      {nodes.map((node, index) => renderBlock(
        node,
        `${index}`,
        t("theme.markdown.tableRegion"),
        t("theme.markdown.task")
      ))}
    </div>
  );
}

/** Renders restricted inline Markdown with the same safety rules as `MarkdownBody`. */
export function MarkdownInline({ nodes, keyPrefix = "inline" }: { nodes: MarkdownInlineNode[]; keyPrefix?: string }) {
  return <>{renderInline(nodes, keyPrefix)}</>;
}

/** Renders inline Markdown inside an existing link, so nested anchors are never produced. */
export function MarkdownInlineText({ nodes, keyPrefix = "inline-text" }: { nodes: MarkdownInlineNode[]; keyPrefix?: string }) {
  return <>{renderInline(flattenLinks(nodes), keyPrefix, true)}</>;
}

function flattenLinks(nodes: MarkdownInlineNode[]): MarkdownInlineNode[] {
  return nodes.flatMap((node): MarkdownInlineNode[] => {
    if (node.type === "link") return flattenLinks(node.children);
    if (node.type === "strong" || node.type === "emphasis" || node.type === "delete") {
      return [{ ...node, children: flattenLinks(node.children) }];
    }
    return [node];
  });
}

function renderBlock(node: MarkdownBlockNode, key: string, tableLabel: string, taskLabel: string): ReactNode {
  switch (node.type) {
    case "paragraph":
      return <p key={key}>{renderInline(node.children, key)}</p>;
    case "blockquote":
      return <blockquote key={key}>{node.children.map((child, index) => renderBlock(child, `${key}-${index}`, tableLabel, taskLabel))}</blockquote>;
    case "list": {
      const List = node.ordered ? "ol" : "ul";
      const listStyle = node.ordered
        ? ({
            "--md-ol-start": node.start ?? 1,
            counterReset: `md-ordered-item ${(node.start ?? 1) - 1}`
          } as CSSProperties)
        : undefined;
      return (
        <List key={key} start={node.ordered ? node.start : undefined} style={listStyle}>
          {node.items.map((item, itemIndex) => (
            <li key={`${key}-${itemIndex}`} className={item.checked === undefined ? undefined : "markdown-task-item"}>
              {item.checked !== undefined && (
                <input
                  type="checkbox"
                  checked={item.checked}
                  aria-label={getTaskLabel(item.children) || taskLabel}
                  readOnly
                  disabled
                  tabIndex={-1}
                />
              )}
              <div className="markdown-list-item-content">
                {item.children.map((child, childIndex) => renderBlock(child, `${key}-${itemIndex}-${childIndex}`, tableLabel, taskLabel))}
              </div>
            </li>
          ))}
        </List>
      );
    }
    case "code":
      return (
        <MarkdownCodeBlock key={key} value={node.value} language={node.language} />
      );
    case "thematicBreak":
      return <hr key={key} />;
    case "table":
      return (
        <div key={key} className="markdown-table-region" role="region" tabIndex={0} aria-label={tableLabel}>
          <table>
            {node.rows.length > 0 && (
              <thead>
                <tr>{node.rows[0].map((cell, index) => <th key={index} style={alignmentStyle(node.align[index])}>{renderInline(cell, `${key}-h-${index}`)}</th>)}</tr>
              </thead>
            )}
            {node.rows.length > 1 && (
              <tbody>
                {node.rows.slice(1).map((row, rowIndex) => (
                  <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex} style={alignmentStyle(node.align[cellIndex])}>{renderInline(cell, `${key}-${rowIndex}-${cellIndex}`)}</td>)}</tr>
                ))}
              </tbody>
            )}
          </table>
        </div>
      );
    case "source":
      return <pre key={key} className="markdown-source-fallback"><code>{node.value}</code></pre>;
  }
}

function renderInline(nodes: MarkdownInlineNode[], keyPrefix: string, insideLink = false): ReactNode[] {
  return nodes.flatMap((node, index): ReactNode[] => {
    const key = `${keyPrefix}-${index}`;
    switch (node.type) {
      case "text":
        return splitTextBreaks(node.value, key);
      case "break":
        return [<br key={key} />];
      case "strong":
        return [<strong key={key}>{renderInline(node.children, key, insideLink)}</strong>];
      case "emphasis":
        return [<em key={key}>{renderInline(node.children, key, insideLink)}</em>];
      case "delete":
        return [<del key={key}>{renderInline(node.children, key, insideLink)}</del>];
      case "inlineCode":
        return [insideLink ? <code key={key}>{node.value}</code> : <MarkdownInlineCode key={key} value={node.value} />];
      case "link":
        return [<a key={key} href={node.href} rel="noreferrer">{renderInline(node.children, key, true)}</a>];
      case "source":
        return [<span key={key} className="markdown-source-fallback-inline">{node.value}</span>];
    }
  });
}

function splitTextBreaks(value: string, key: string): ReactNode[] {
  return value.split("\n").flatMap((part, index, parts) => [
    <Fragment key={`${key}-text-${index}`}>{part}</Fragment>,
    ...(index < parts.length - 1 ? [<br key={`${key}-break-${index}`} />] : [])
  ]);
}

function getTaskLabel(nodes: MarkdownBlockNode[]): string {
  const paragraph = nodes.find((node): node is Extract<MarkdownBlockNode, { type: "paragraph" }> => node.type === "paragraph");
  return paragraph ? getInlineLabel(paragraph.children).trim() : "";
}

function getInlineLabel(nodes: MarkdownInlineNode[]): string {
  return nodes.map((node) => {
    switch (node.type) {
      case "text":
      case "inlineCode":
      case "source":
        return node.value;
      case "break":
        return " ";
      case "strong":
      case "emphasis":
      case "delete":
      case "link":
        return getInlineLabel(node.children);
    }
  }).join("");
}

function alignmentStyle(align: MarkdownTableAlign | undefined): { textAlign: "left" | "center" | "right" } | undefined {
  return align ? { textAlign: align } : undefined;
}
